import {Observable} from 'rxjs';
import {change, formValueSelector} from 'redux-form';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import RecipientsActions from 'web-app/react/entities/recipients/actions';
import * as RecipientsObservables from 'web-app/react/entities/recipients/epics';
import RecipientActionTypes from 'web-app/react/entities/recipients/action-types';
import {showAlertObservable} from 'web-app/react/dialog';
import {getRecipientString} from 'web-app/util/string';
import * as GlobalSearchApi from 'web-app/react/entities/global-search/api';
import {EntityTypes} from 'web-app/react/entities/global-search/model';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Actions from './actions';
import * as SentryService from '../../../../services/sentry';

const initialFetch = action$ =>
    action$.ofType(Actions.initialFetch.type).mergeMap(({payload}) => {
        const {recipientsType, context, initialValues} = payload;

        return RecipientsObservables.fetchSendersObservable(recipientsType, context)
            .mergeMap(sendersSuccessAction => {
                const initialFrom =
                    initialValues && initialValues.from
                        ? sendersSuccessAction.payload.senders.find(rec => rec.id === initialValues.from)
                        : null;
                const initialSender = initialFrom
                    ? initialFrom
                    : sendersSuccessAction.payload.senders.find(rec => rec.isDefault);

                const sendersSuccessObservable = Observable.of(sendersSuccessAction);
                const successObservable = Observable.of(Actions.initialFetchSuccess.action());

                if (initialSender) {
                    return Observable.concat(
                        sendersSuccessObservable,
                        RecipientsObservables.fetchRecipientsObservable(recipientsType, {from: initialSender.id}),
                        successObservable,
                    );
                } else {
                    return Observable.concat(sendersSuccessObservable, successObservable);
                }
            })
            .catch(e => Observable.of(Actions.initialFetchFailed.action(e)));
    });

const fetchRecipients = action$ =>
    action$.ofType(Actions.changeFrom.type).map(({payload}) => {
        const {form, type, currentRecipients, extraData, newSender, onSuccess} = payload;

        if (!extraData || !extraData.from) {
            SentryService.captureMessage(
                `Illegal from value when potentially calling v2/posts/to: ${extraData && extraData.from}`,
            );
        }

        return (RecipientsActions.fetch as any)(type, extraData, {
            form,
            newSender,
            oldRecipients: currentRecipients,
            onSuccess,
        });
    });

const filterRecipients = (action$, state$) =>
    action$
        .ofType(RecipientActionTypes.RECIPIENTS_FETCH_SUCCESS)
        .map(action => {
            const {successData} = action.payload;

            let formRecipients;

            if (successData && successData.form) {
                const selector = formValueSelector(successData.form);
                formRecipients = selector(state$.value, 'selectedRecipients');
            }

            return {
                ...action,
                formRecipients,
            };
        })
        .filter(({formRecipients}) => Boolean(formRecipients))
        .mergeMap(({payload, formRecipients}) => {
            const {successData, recipients} = payload;
            const {form, oldRecipients, newSender} = successData;

            const selectedRecipients = formRecipients.split(',');
            const newRecipients = selectedRecipients.filter(id => recipients.some(recipient => recipient.id === id));

            if (selectedRecipients.length !== newRecipients.length) {
                const removedRecipients = oldRecipients
                    .filter(newRecipient => {
                        const isInOld = selectedRecipients.some(id => newRecipient.id === id);
                        const isInNew = recipients.some(recipient => recipient.id === newRecipient.id);
                        return isInOld && !isInNew;
                    })
                    .map(rec => rec.title);

                const dialogObservable = showAlertObservable({
                    title: '',
                    description: i18next.t('feed.invalidRecipients', {
                        sender: newSender.label,
                        removedRecipients: getRecipientString(removedRecipients.toArray()),
                    }),
                }).filter(() => false);

                return Observable.merge(
                    dialogObservable,
                    Observable.of(change(form, 'selectedRecipients', newRecipients.join(','))),
                );
            } else {
                return Observable.empty();
            }
        });

export const searchChildrenObservable = (term, institutionIds) => {
    if (hasValue(term)) {
        return GlobalSearchApi.search(term, [EntityTypes.Child], institutionIds).map(({searchItems}) =>
            Actions.searchChildrenSuccess.action({searchResults: searchItems}),
        );
    }

    return Observable.of(Actions.searchChildrenAborted.action());
};

const searchChildren = action$ =>
    action$
        .ofType(Actions.searchChildren.type)
        .debounceTime(500)
        .mergeMap(({payload}) =>
            searchChildrenObservable(payload.term, payload.institutionIds).catch(e =>
                Observable.of(Actions.searchChildrenFailed.action(e)),
            ),
        );

const addEYFSLinks = action$ =>
    action$.ofType(Actions.addEYFSLinks.type).mergeMap(({payload}) => {
        const {
            refinements: {statementRefinements, areaRefinements},
            formIdentifier,
        } = payload;

        const selectedStatementRefinements = Object.keys(statementRefinements)
            .map(key => ({
                refinement:
                    statementRefinements[key] && statementRefinements[key].refinement
                        ? statementRefinements[key].refinement
                        : null,
                statementId: key,
            }))
            .filter(link => statementRefinements[link.statementId] && statementRefinements[link.statementId].checked);

        const selectedAreaRefinements = Object.values(areaRefinements)
            .filter(a => a)
            .map((areaRefinement: any) => ({
                areaId: areaRefinement.refinedEntityId,
                refinement: areaRefinement.refinement,
            }));

        return Observable.concat(
            Observable.of(change(formIdentifier, 'statements', selectedStatementRefinements)),
            Observable.of(change(formIdentifier, 'areaRefinements', selectedAreaRefinements)),
        );
    });

export default combineEpics(initialFetch, fetchRecipients, searchChildren, addEYFSLinks, filterRecipients);
